// @flow
import React, { useContext, useEffect, useState } from 'react';
import { Redirect, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import {
  AppBar,
  Box,
  Button,
  CircularProgress,
  Container,
  CssBaseline,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Toolbar,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  Build as DraftIcon,
  HourglassEmpty as ValidatingIcon,
  NavigateNext as NextIcon,
  WhatsApp as SupportIcon,
  Work as ProcessingIcon,
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  PictureAsPdf as PdfIcon,
} from '@material-ui/icons';
import routes from '../routes';
import ProcessAgreementData from '../component/ProcessAgreementData';
import ProcessCompanyData from '../component/ProcessCompanyData';
import ProcessPersonalDataStepper from '../component/ProcessPersonalDataStepper';
import ProcessVideoData from '../component/ProcessVideoData';
import ApiResponse from '../model/ApiResponse';
import SignProcess, { ProcessResponse } from '../model/SignProcess';
import SignProcessCompany from '../model/SignProcessCompany';
import { type AgreementProps } from '../model/props';
import { toSentence } from '../util/string';
import { whatsappURL } from '../util/vendor';
import { AuthContext, setAuthToken } from '../context/auth';
import { doPostMultipart } from '../util/api';

const useStyles = makeStyles((theme) => ({
  paper: {
    marginTop: theme.spacing(2),
  },
  appBarContent: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
  },
  logo: {
    height: 32,
    marginRight: theme.spacing(2),
  },
  brandContainer: {
    display: 'flex',
    alignItems: 'center',
    flexGrow: 1,
  },
  brandText: {
    color: theme.palette.text.primary,
    fontWeight: 500,
  },
  poweredBy: {
    fontSize: theme.typography.pxToRem(12),
    color: theme.palette.text.secondary,
    marginLeft: 'auto',
  },
  draft: {
    color: theme.palette.secondary['900'],
  },
  validating: {
    color: theme.palette.grey['700'],
  },
  processing: {
    color: theme.palette.grey['900'],
  },
  grey: {
    color: theme.palette.text.secondary,
  },
  error: {
    color: theme.palette.error['600'],
    fontWeight: 'bold',
    fontSize: theme.typography.pxToRem(14),
  },
  heading: {
    fontSize: theme.typography.pxToRem(15),
    flexBasis: '33.33%',
    flexShrink: 0,
  },
  secondaryHeading: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.text.secondary,
  },
  bottomButton: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
  },
  nextStepButton: {
    background: 'linear-gradient(45deg, #2196F3 30%, #021c49 90%)',
    border: 0,
    borderRadius: 25,
    boxShadow: '0 3px 5px 2px rgba(33, 203, 243, .3)',
    color: 'white',
    height: 56,
    padding: '0 30px',
    fontSize: '1.1rem',
    fontWeight: 'bold',
    textTransform: 'none',
    transition: 'all 0.3s ease-in-out',
    '&:hover': {
      background: 'linear-gradient(45deg, #1976D2 30%, #021c49 90%)',
      boxShadow: '0 6px 10px 4px rgba(33, 203, 243, .3)',
      transform: 'translateY(-2px)',
    },
    '&:active': {
      transform: 'translateY(0)',
    },
  },
  nextStepContainer: {
    background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
    borderRadius: theme.spacing(2),
    padding: theme.spacing(3),
    textAlign: 'center',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    border: '1px solid #e0e0e0',
  },
  nextStepText: {
    marginBottom: theme.spacing(2),
    color: theme.palette.text.primary,
    fontWeight: 500,
  },
  nextStepSubtext: {
    marginBottom: theme.spacing(2),
    color: theme.palette.text.secondary,
    fontSize: '0.95rem',
  },
  pulseAnimation: {
    animation: '$pulse 2s infinite',
  },
  '@keyframes pulse': {
    '0%': {
      boxShadow: '0 0 0 0 rgba(33, 150, 243, 0.7)',
    },
    '70%': {
      boxShadow: '0 0 0 10px rgba(33, 150, 243, 0)',
    },
    '100%': {
      boxShadow: '0 0 0 0 rgba(33, 150, 243, 0)',
    },
  },
  supportContainer: {
    background: 'linear-gradient(135deg, #e8f5e8 0%, #f1f8e9 100%)',
    borderRadius: theme.spacing(2),
    padding: theme.spacing(2),
    margin: theme.spacing(1, 0),
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    border: '1px solid #c8e6c9',
    transition: 'all 0.3s ease-in-out',
    '&:hover': {
      transform: 'translateY(-1px)',
      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
    },
  },
  supportButton: {
    background: 'linear-gradient(45deg, #4CAF50 30%, #2E7D32 90%)',
    color: 'white',
    borderRadius: 20,
    padding: theme.spacing(1.5, 3),
    fontSize: '1rem',
    fontWeight: '500',
    textTransform: 'none',
    boxShadow: '0 2px 4px rgba(76, 175, 80, 0.3)',
    transition: 'all 0.3s ease-in-out',
    '&:hover': {
      background: 'linear-gradient(45deg, #45a049 30%, #1B5E20 90%)',
      boxShadow: '0 4px 8px rgba(76, 175, 80, 0.4)',
      transform: 'translateY(-1px)',
    },
  },
  supportIcon: {
    color: 'white',
    marginRight: theme.spacing(1),
  },
  supportText: {
    color: 'white',
    fontWeight: '500',
  },
  supportTitle: {
    color: theme.palette.text.primary,
    fontWeight: '600',
    marginBottom: theme.spacing(1),
  },
  timeoutLoader: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '400px',
    textAlign: 'center',
    padding: theme.spacing(4),
  },
  timeoutText: {
    marginTop: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
  timeoutProgress: {
    width: '100%',
    maxWidth: 400,
    marginTop: theme.spacing(3),
  },
  timeoutCounter: {
    marginTop: theme.spacing(2),
    fontSize: theme.typography.pxToRem(24),
    fontWeight: 'bold',
    color: theme.palette.primary.main,
  },
  fieldWithImage: {
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'flex-start',
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  fieldContainer: {
    flex: 1,
  },
  referenceImage: {
    width: 200,
    height: 'auto',
    borderRadius: theme.spacing(1),
    border: `2px solid ${theme.palette.primary.main}`,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    cursor: 'pointer',
    transition: 'transform 0.2s',
    '&:hover': {
      transform: 'scale(1.05)',
    },
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      maxWidth: 300,
    },
  },
  imageCaption: {
    fontSize: theme.typography.pxToRem(12),
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
    fontStyle: 'italic',
  },
  helpButton: {
    color: theme.palette.primary.main,
  },
  referenceImageDialog: {
    width: '100%',
    maxWidth: 500,
    height: 'auto',
    borderRadius: theme.spacing(1),
  },
  dialogImage: {
    textAlign: 'center',
    padding: theme.spacing(2),
  },
}));

type Props = {
  isRetailClient: boolean,
  match?: any,
};

const baseUrl = process.env.PUBLIC_URL || '';
const personalData = 'personal-data';
const companyData = 'company-data';
const agreementData = 'agreement-data';
const videoData = 'video-data';

const getExpandedKey = (process, user) => {
  if (!!process) {
    if (process.hasDocuments() && !process.hasDataLoaded()) {
      return personalData;
    } else if (process.haveCompanyData && !process.completedCompanyData) {
      return companyData;
    } else if (!process.hasAgreementLoaded()) {
      return agreementData;
    } else if (!process.hasVideoLoaded()) {
      return videoData;
    }
    return videoData;
  }
};
const maxSize = Number.parseInt(`${process.env.REACT_APP_UPLOAD_LIMIT || '0'}`);

const DocumentValidation = (props: Props) => {
  const { doGet, doPost, user } = useContext(AuthContext);
  const { t } = useTranslation();
  const { match, isRetailClient } = props;
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const [supportPhone, setSupportPhone] = useState<?string>();
  const [agentPhone, setAgentPhone] = useState<?string>();
  const [error, setError] = useState<?string>();

  const classes = useStyles();
  const [process, setProcess] = useState<?SignProcess>();
  const [expanded, setExpanded] = useState<?string>();
  const [processing, setProcessing] = useState<boolean>(false);
  const [rucModalOpen, setRucModalOpen] = useState(false);
  const [rucFile, setRucFile] = useState<?File>();
  const [rucThumbnail, setRucThumbnail] = useState<?string>();
  const [uploadingRuc, setUploadingRuc] = useState(false);
  const [rucSubmit, setRucSubmit] = useState(false);

  useEffect(() => {
    if (!rucSubmit) {
      const expandedKey = getExpandedKey(process, user);
      setExpanded(expandedKey);
    }
  }, [process, user, rucSubmit]);

  const loadData = () => {
    console.log(error);

    if (!process && match && match.params && match.params.code && match.params.token) {
      let path = 'retail';
      setAuthToken(null);
      if (!isRetailClient) {
        setAuthToken(match.params.token);
        path = 'token';
      }
      doGet(`/customer/${path}-sign-process/${match.params.code}/${match.params.token}`).then(
        (response: ApiResponse) => {
          if (response.ok) {
            const theProcess = SignProcess.parse(response.data);
            setProcess(theProcess);
            const expandedKey = getExpandedKey(theProcess, user);
            setExpanded(expandedKey);

            // Check if there are no files and show RUC modal
            if (theProcess.files.length === 0) {
              setRucModalOpen(true);
            }
          } else {
            setError(response.message || 'Error');
          }
        }
      );
      if (!supportPhone) {
        doGet('/customer/support-phone').then((response: ApiResponse) => setSupportPhone(response.data));
      }
      if (!agentPhone) {
        doGet('/customer/agent-phone').then((response: ApiResponse) => setAgentPhone(response.data));
      }
    }
  };

  const handleExpanded = (key: string) => (event, isExpanded: boolean) => {
    setExpanded(isExpanded ? key : null);
  };

  useEffect(loadData, []);

  const saveProcessData = (processData: SignProcess, rucSubmit1: boolean) => {
    setRucSubmit(rucSubmit1);

    // Guardar directamente sin confirmación
    if (!!process) {
      setProcessing(true);

      doPost(`/customer/vendor-sign-process/${process.code}`, processData.toDataMap()).then((response: ApiResponse) => {
        if (response.ok) {
          let theProcess = SignProcess.parse(response.data);
          setProcess(theProcess);
          if (!rucSubmit1) {
            const expandedKey = getExpandedKey(theProcess, user);
            setExpanded(expandedKey);
          }

          enqueueSnackbar(t('Data saved'), {
            variant: 'success',
          });
        } else if (!response.is401Error()) {
          enqueueSnackbar(t(response.message), {
            variant: 'error',
          });
        }
        setProcessing(false);
      });
    }
  };

  const saveCompanyData = (companyData: SignProcessCompany) => {
    if (!!process) {
      setProcessing(true);
      doPost(`/customer/sign-process/${process.code}/company`, companyData.toDataMap()).then(
        (response: ApiResponse) => {
          if (response.ok) {
            let theProcess = SignProcess.parse(response.data);
            setProcess(theProcess);
            const expandedKey = getExpandedKey(theProcess, user);
            setExpanded(expandedKey);
            enqueueSnackbar(t('Data saved'), {
              variant: 'success',
            });
          } else if (!response.is401Error()) {
            enqueueSnackbar(t(response.message), {
              variant: 'error',
            });
          }
          setProcessing(false);
        }
      );
    }
  };

  const saveAgreementData = (data: AgreementProps): Promise<boolean> => {
    if (!!process) {
      setProcessing(true);
      return new Promise((resolve) => {
        process.saveAgreement({ ...data }).then((response: ProcessResponse) => {
          if (response.ok) {
            setProcess(response.process);
            const expandedKey = getExpandedKey(response.process, user);
            setExpanded(expandedKey);
            enqueueSnackbar(t('Contract agreement saved'), {
              variant: 'success',
            });
          } else if (!!response.message) {
            enqueueSnackbar(t(response.message), {
              variant: 'error',
            });
          }
          setProcessing(false);
          resolve(response.ok);
        });
      });
    }
    return new Promise<boolean>((resolve) => resolve(false));
  };

  const saveVideoData = ({ file }): Promise<boolean> => {
    if (!!process) {
      setProcessing(true);
      return new Promise<boolean>((resolve) => {
        process.saveVideo(file).then((response: ApiResponse) => {
          if (response.ok) {
            let theProcess = SignProcess.parse(response.data);
            setProcess(theProcess);
            const expandedKey = getExpandedKey(theProcess, user);
            setExpanded(expandedKey);
            enqueueSnackbar(t('Verification saved'), {
              variant: 'success',
            });
            enqueueSnackbar(t('Your process is VIDEO_LOADED'), {
              variant: 'success',
            });
            setExpanded(null);
          } else if (!response.is401Error()) {
            enqueueSnackbar(t(response.message), {
              variant: 'error',
            });
          }
          setProcessing(false);
          resolve(response.ok);
        });
      });
    }
    return new Promise<boolean>((resolve) => resolve(false));
  };

  const generateRucThumbnail = (file: File) => {
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setRucThumbnail(e.target?.result);
      };
      reader.readAsDataURL(file);
    } else if (file.type === 'application/pdf') {
      setRucThumbnail('pdf');
    }
  };

  const handleRucFileChange = (e: any) => {
    if (e.target.files && e.target.files.length) {
      const file = e.target.files[0];
      if (file.size >= maxSize) {
        enqueueSnackbar(t('Upload limit is MB_SIZE', { size: (maxSize / (1024 * 1024)).toFixed(0) }), {
          variant: 'error',
        });
      } else {
        setRucFile(file);
        generateRucThumbnail(file);
      }
    }
  };

  const handleUploadRuc = () => {
    if (!!process && !!rucFile) {
      setUploadingRuc(true);
      const data = {
        name: rucFile.name,
        type: 'EC_RUC',
      };
      const files = {
        file: rucFile,
      };
      doPostMultipart(`/customer/sign-process/${process.code}/document`, data, files).then((response: ApiResponse) => {
        if (response.ok) {
          let theProcess = SignProcess.parse(response.data);
          setProcess(theProcess);
          const expandedKey = getExpandedKey(theProcess, user);
          setExpanded(expandedKey);
          enqueueSnackbar(t('Document saved'), {
            variant: 'success',
          });
          setRucModalOpen(false);
          setRucFile(null);
          setRucThumbnail(null);
        } else if (!response.is401Error()) {
          enqueueSnackbar(t(response.message), {
            variant: 'error',
          });
        }
        setUploadingRuc(false);
      });
    }
  };

  if (!match || !match.params || !match.params.code) {
    return <Redirect to={routes.home} />;
  }

  return (
    <Container component="main" maxWidth="xl">
      <CssBaseline />
      <AppBar position="static" color="default" elevation={1}>
        <Toolbar>
          <Box className={classes.appBarContent}>
            <div className={classes.brandContainer}>
              <img src={`${baseUrl}/images/logo.png`} alt="Logo" className={classes.logo} />
              <div>
                <Typography variant="h6" className={classes.brandText}>
                  {t('Validation Data')}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {t('EcuaFirma Digital Signature Process')}
                </Typography>
              </div>
            </div>
            <Typography variant="caption" className={classes.poweredBy}>
              {t('Powered by eQon')}
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>

      <div className={classes.paper}>
        {!!process && (
          <>
            <Typography variant="h5">
              {process.isDraft() && <DraftIcon className={classes.draft} />}
              {process.hasVideoLoaded() && !process.isProcessing() && !process.isCompleted() && (
                <ValidatingIcon className={classes.draft} />
              )}
              {(process.isProcessing() || process.isCompleted()) && <ProcessingIcon className={classes.processing} />}
              &nbsp;{t(toSentence(process.processType))} <small>({t(`Your process is ${process.state}`)})</small>
            </Typography>
            <Typography variant="subtitle2" className={classes.grey}>
              {t('Duration')}: {process.durationValue()} {t(process.durationLabel()).toLowerCase()}.
            </Typography>
            {!!process.containerTitle && (
              <Typography variant="subtitle2" className={classes.grey}>
                {t('Container')}: {process.containerTitle} ${process.containerPrice.toFixed(2)}.
              </Typography>
            )}
            <Typography variant="subtitle2" className={classes.grey}>
              {t('Total price')}: ${process.price.toFixed(2)}.
            </Typography>
            <small>&nbsp;</small>

            {/* Then show personal data stepper */}
            {!!process && (
              <ProcessPersonalDataStepper
                code={personalData}
                disabled={processing || process.hasDocumentsValidated()}
                onSave={saveProcessData}
                expanded={expanded === personalData}
                onExpanded={(key) => handleExpanded(key)}
                tuFacturero={true}
                process={process}
              />
            )}

            {/* Company data if needed */}
            {!!process && process.haveCompanyData && !!process.hasDocuments() && (
              <ProcessCompanyData
                code={companyData}
                disabled={processing || process.hasDocumentsValidated()}
                onSave={saveCompanyData}
                expanded={expanded === companyData}
                onExpanded={(key) => handleExpanded(key)}
                parent={process}
                process={process.companyData ?? new SignProcessCompany()}
              />
            )}

            {/* Agreement data */}
            {!!process && !!process.hasDataLoaded() && (
              <ProcessAgreementData
                isRetailClient={false}
                code={agreementData}
                disabled={processing || process.hasDocumentsValidated()}
                processing={processing}
                onSave={saveAgreementData}
                expanded={expanded === agreementData}
                onExpanded={(key) => handleExpanded(key)}
                contractAgreementUrl={process.contractAgreementUrl}
                templateAgreementUrl={process.templateAgreementUrl}
                process={process}
              />
            )}

            {/* Video data */}
            {!!process &&
              !!process.hasDataLoaded() &&
              !!process.contractAgreementUrl && (
                <ProcessVideoData
                  code={videoData}
                  disabled={processing || process.hasDocumentsValidated() }
                  processing={processing}
                  onSave={saveVideoData}
                  expanded={expanded === videoData}
                  onExpanded={(key) => handleExpanded(key)}
                  process={process}
                />
              )}

            {!!process &&
              !process.hasDataLoaded() &&
              !!process.hasDataLoaded() &&
              !!process.contractAgreementUrl &&
              !!user &&
              !user.isRetail && (
                <Box className={classes.bottomButton}>
                  <div className={classes.nextStepContainer}>
                    <Typography variant="h6" className={classes.nextStepText}>
                      {t('Ready to continue?')}
                    </Typography>
                    <Typography variant="body2" className={classes.nextStepSubtext}>
                      {t('Complete your digital signature process in the next step')}
                    </Typography>
                    <Button
                      variant="contained"
                      fullWidth
                      startIcon={<NextIcon />}
                      className={`${classes.nextStepButton} ${classes.pulseAnimation}`}
                      onClick={() => {
                        history.push(`/process/${process?.code || ''}`);
                      }}>
                      {t('Continue Process')}
                    </Button>
                  </div>
                </Box>
              )}

            {/* Agent Support */}
            <Box className={classes.supportContainer}>
              <Typography variant="subtitle1" className={classes.supportTitle}>
                {t('Need technical support?')}
              </Typography>
              <Button
                fullWidth
                startIcon={<SupportIcon className={classes.supportIcon} />}
                className={classes.supportButton}
                onClick={() => {
                  const url = whatsappURL(agentPhone, t('Support text', user));
                  window.open(url, '_blank');
                }}>
                <span className={classes.supportText}>{t('Contact Support via WhatsApp')}</span>
              </Button>
            </Box>
          </>
        )}
      </div>

      {/* RUC Upload Modal */}
      <Dialog open={rucModalOpen} maxWidth="sm" fullWidth disableBackdropClick={true} disableEscapeKeyDown={true}>
        <DialogTitle>{t('Upload RUC Document')}</DialogTitle>
        <DialogContent>
          <DialogContentText style={{ marginBottom: '20px', fontWeight: 500 }}>
            {t('Para comenzar el proceso, necesitamos que suba su documento RUC (Registro Único de Contribuyentes).')}
          </DialogContentText>

          <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
            {/* Example thumbnail */}
            <Box display="flex" flexDirection="column" alignItems="center">
              <div
                style={{
                  width: 180,
                  height: 120,
                  borderRadius: 8,
                  marginBottom: 12,
                  border: '3px solid #2196F3',
                  overflow: 'hidden',
                  position: 'relative',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                }}>
                <img
                  src={`${baseUrl}/images/ruc.png`}
                  alt="RUC Example"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                  onError={(e) => {
                    e.target.src = `${baseUrl}/images/document.png`;
                  }}
                />
                <div
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    background: 'linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.8) 100%)',
                    color: 'white',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    padding: '8px 6px 6px',
                    textAlign: 'center',
                  }}>
                  {t('RUC Example')}
                </div>
              </div>
              <Typography variant="caption" color="textSecondary" align="center">
                {t('Photo of tax registration')}
              </Typography>
            </Box>

            {/* Upload area or uploaded file */}
            {!rucFile ? (
              <Box
                style={{
                  width: '100%',
                  padding: '24px',
                  border: '2px dashed #ccc',
                  borderRadius: 8,
                  textAlign: 'center',
                  backgroundColor: '#fafafa',
                  cursor: 'pointer',
                }}>
                <input
                  accept=".png,.jpg,.pdf"
                  style={{ display: 'none' }}
                  id="ruc-file-upload"
                  type="file"
                  onChange={handleRucFileChange}
                  disabled={uploadingRuc}
                />
                <label htmlFor="ruc-file-upload" style={{ cursor: 'pointer', width: '100%', display: 'block' }}>
                  <UploadIcon style={{ fontSize: 48, color: '#666', marginBottom: 8 }} />
                  <Typography variant="body2" gutterBottom>
                    {t('Click to select RUC document')}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {t('Photo in JPG, PNG or PDF format')}
                  </Typography>
                </label>
              </Box>
            ) : (
              <Box display="flex" flexDirection="column" alignItems="center" width="100%">
                <Box position="relative" marginBottom={1}>
                  {rucThumbnail === 'pdf' ? (
                    <Box
                      style={{
                        width: 120,
                        height: 80,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#f5f5f5',
                        borderRadius: 8,
                        border: '2px solid #4CAF50',
                      }}>
                      <PdfIcon style={{ fontSize: 32, color: '#d32f2f' }} />
                      <Typography variant="caption" color="textSecondary">
                        PDF
                      </Typography>
                    </Box>
                  ) : rucThumbnail ? (
                    <Box
                      style={{
                        width: 120,
                        height: 80,
                        borderRadius: 8,
                        border: '2px solid #4CAF50',
                        overflow: 'hidden',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#fff',
                      }}>
                      <img
                        src={rucThumbnail}
                        alt="RUC Preview"
                        style={{
                          maxWidth: '100%',
                          maxHeight: '100%',
                          objectFit: 'contain',
                        }}
                      />
                    </Box>
                  ) : null}
                  <div
                    style={{
                      position: 'absolute',
                      top: -8,
                      right: -8,
                      backgroundColor: '#4CAF50',
                      color: 'white',
                      borderRadius: '50%',
                      width: 24,
                      height: 24,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.7rem',
                      fontWeight: 'bold',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                    }}>
                    ✓
                  </div>
                </Box>
                <Typography
                  variant="caption"
                  align="center"
                  style={{ marginBottom: 8, maxWidth: 200, wordBreak: 'break-word' }}>
                  {rucFile.name}
                </Typography>
                <Button
                  size="small"
                  onClick={() => {
                    setRucFile(null);
                    setRucThumbnail(null);
                  }}
                  disabled={uploadingRuc}
                  startIcon={<DeleteIcon />}
                  color="secondary">
                  {t('Remove')}
                </Button>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleUploadRuc}
            color="primary"
            variant="contained"
            disabled={!rucFile || uploadingRuc}
            startIcon={uploadingRuc ? <CircularProgress size={16} color="inherit" /> : <UploadIcon />}>
            {uploadingRuc ? t('Uploading...') : t('Upload')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default DocumentValidation;
